import { hash } from 'lib/miscellaneous';

/**
 * One colour per avatar, in both forms: Tailwind classes for the DOM, and solid hexes for
 * surfaces that cannot use classes (the org chart minimap and its SVG export). The hexes are
 * the light theme values of the same Tailwind shades, so both forms read as the same colour.
 */
export interface AvatarColor {
  bg: string;
  text: string;
  hex: string;
  textHex: string;
}

export const AVATAR_PALETTE: AvatarColor[] = [
  { bg: 'bg-red-100', text: 'text-red-700', hex: '#fee2e2', textHex: '#b91c1c' },
  { bg: 'bg-orange-100', text: 'text-orange-700', hex: '#ffedd5', textHex: '#c2410c' },
  { bg: 'bg-amber-100', text: 'text-amber-700', hex: '#fef3c7', textHex: '#b45309' },
  { bg: 'bg-lime-100', text: 'text-lime-700', hex: '#ecfccb', textHex: '#4d7c0f' },
  { bg: 'bg-green-100', text: 'text-green-700', hex: '#dcfce7', textHex: '#15803d' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700', hex: '#d1fae5', textHex: '#047857' },
  { bg: 'bg-teal-100', text: 'text-teal-700', hex: '#ccfbf1', textHex: '#0f766e' },
  { bg: 'bg-cyan-100', text: 'text-cyan-700', hex: '#cffafe', textHex: '#0e7490' },
  { bg: 'bg-sky-100', text: 'text-sky-700', hex: '#e0f2fe', textHex: '#0369a1' },
  { bg: 'bg-blue-100', text: 'text-blue-700', hex: '#dbeafe', textHex: '#1d4ed8' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700', hex: '#e0e7ff', textHex: '#4338ca' },
  { bg: 'bg-violet-100', text: 'text-violet-700', hex: '#ede9fe', textHex: '#6d28d9' },
  { bg: 'bg-purple-100', text: 'text-purple-700', hex: '#f3e8ff', textHex: '#7e22ce' },
  { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700', hex: '#fae8ff', textHex: '#a21caf' },
  { bg: 'bg-pink-100', text: 'text-pink-700', hex: '#fce7f3', textHex: '#be185d' },
  { bg: 'bg-rose-100', text: 'text-rose-700', hex: '#ffe4e6', textHex: '#be123c' },
];

// Blue is what an avatar falls back to when there is no id to hash.
const FALLBACK_INDEX = 9;

/**
 * Picks the colour for a person (or team, or company) from their id. Uses the hash the app has
 * always used for avatars, so nobody's circle changes colour.
 */
export const colorForId = (id?: string | null): AvatarColor => {
  if (!id) {
    return AVATAR_PALETTE[FALLBACK_INDEX];
  }
  return AVATAR_PALETTE[hash(id) % AVATAR_PALETTE.length];
};
